import React, { useState, useEffect } from 'react';
import {
    Grid,
    Box,
    Typography,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Stack,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    BarChart,
    Bar,
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import config from '../config';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const toTitleCase = (str) => {
    if (!str) return str;
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const formatCurrency = (value) => {
    if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
    } else {
        return `$${value.toFixed(0)}`;
    }
};

// Custom X-Axis tick with 45-degree rotation and title case
const CustomXAxisTick = ({ x, y, payload }) => {
    const titleCase = toTitleCase(payload.value);

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={16}
                textAnchor="end"
                fill="#64748b"
                fontSize="11px"
                transform="rotate(-45)"
            >
                {titleCase}
            </text>
        </g>
    );
};

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const dataPoint = payload[0].payload;
        return (
            <Box
                sx={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.5rem',
                    p: 1.5,
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
            >
                <Typography sx={{ fontSize: '0.813rem', fontWeight: 600, mb: 0.5, color: '#1e293b' }}>
                    {dataPoint.name}
                </Typography>

                {payload.map((entry, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Box sx={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', bgcolor: entry.color }} />
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {entry.name}: <strong style={{ color: '#1e293b' }}>
                                {entry.name === 'Patient Volume' || entry.dataKey === 'volume'
                                    ? entry.value.toLocaleString()
                                    : entry.name === 'Avg Charge' || entry.dataKey === 'avgCharge'
                                    ? `$${entry.value.toLocaleString()}`
                                    : formatCurrency(entry.value)}
                            </strong>
                        </Typography>
                    </Box>
                ))}

                {/* Show adjustments if present in data - with divider above */}
                {dataPoint.adjustments !== undefined && dataPoint.adjustments !== null && (
                    <>
                        <Box sx={{ borderTop: '1px solid #e2e8f0', my: 0.75 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                            <Box sx={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', bgcolor: '#f97316' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                                Adjustments: <strong style={{ color: '#1e293b' }}>
                                    {formatCurrency(dataPoint.adjustments)}
                                </strong>
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        );
    }
    return null;
};

function PerformanceAnalysis({ filters, consolidateLocations = true }) {
    // Loading and error states for each section
    const [cptProviderLoading, setCptProviderLoading] = useState(false);
    const [cptLocationLoading, setCptLocationLoading] = useState(false);
    const [providerLoading, setProviderLoading] = useState(true);
    const [locationLoading, setLocationLoading] = useState(true);

    // API data states
    const [cptProviderData, setCptProviderData] = useState([]);
    const [cptLocationData, setCptLocationData] = useState([]);
    const [providerRevenueData, setProviderRevenueData] = useState([]);
    const [providerVolumeData, setProviderVolumeData] = useState([]);
    const [providerAvgChargeData, setProviderAvgChargeData] = useState([]);
    const [providerCptData, setProviderCptData] = useState([]);
    const [locationRevenueData, setLocationRevenueData] = useState([]);
    const [locationVolumeData, setLocationVolumeData] = useState([]);
    const [locationAvgChargeData, setLocationAvgChargeData] = useState([]);
    const [locationCptData, setLocationCptData] = useState([]);

    // Metadata for dropdown options
    const [availableCpts, setAvailableCpts] = useState([]);
    const [availableProviders, setAvailableProviders] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);

    // Local filters for CPT + Provider chart
    const [cptProviderYear, setCptProviderYear] = useState(new Date().getFullYear().toString());
    const [cptProviderMonth, setCptProviderMonth] = useState(null);
    const [cptProviderCpt, setCptProviderCpt] = useState('All');
    const [cptProviderProvider, setCptProviderProvider] = useState('All');

    // Local filters for CPT + Location chart
    const [cptLocationYear, setCptLocationYear] = useState(new Date().getFullYear().toString());
    const [cptLocationMonth, setCptLocationMonth] = useState(null);
    const [cptLocationCpt, setCptLocationCpt] = useState('All');
    const [cptLocationLocation, setCptLocationLocation] = useState('All');

    // Active metric for Provider Performance
    const [providerMetric, setProviderMetric] = useState('revenue');

    // Active metric for Location Performance
    const [locationMetric, setLocationMetric] = useState('revenue');

    // Filters for Provider Performance
    const [providerYear, setProviderYear] = useState(new Date().getFullYear().toString());
    const [providerMonth, setProviderMonth] = useState(null);

    // Filters for Location Performance
    const [locationYear, setLocationYear] = useState(new Date().getFullYear().toString());
    const [locationMonth, setLocationMonth] = useState(null);

    // Fetch available years on mount
    useEffect(() => {
        const fetchYears = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${config.apiBaseUrl}/api/available-years`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setAvailableYears(data.years || []);
                    // Set all year filters to current year
                    if (data.years && data.years.length > 0) {
                        const currentYear = new Date().getFullYear().toString();
                        setCptProviderYear(currentYear);
                        setCptLocationYear(currentYear);
                        setProviderYear(currentYear);
                        setLocationYear(currentYear);
                    }
                }
            } catch (err) {
                console.error('Error fetching available years:', err);
            }
        };

        fetchYears();
    }, []);

    // Reset location filter when consolidateLocations changes (selected value may not exist in new list)
    useEffect(() => {
        setCptLocationLocation('All');
    }, [consolidateLocations]);

    // Fetch metadata on mount
    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const queryParams = new URLSearchParams();
                queryParams.append('year', cptProviderYear || '2025');
                queryParams.append('section', 'cptProvider');
                queryParams.append('dateType', filters?.dateType || 'post');
                queryParams.append('consolidateLocations', consolidateLocations.toString());

                const token = localStorage.getItem('token');
                const response = await fetch(`${config.apiBaseUrl}/api/performance-analysis?${queryParams}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) throw new Error(`Failed to fetch metadata: ${response.statusText}`);

                const data = await response.json();
                setAvailableCpts(data.availableCpts || []);
                setAvailableProviders(data.availableProviders || []);
                setAvailableLocations(data.availableLocations || []);
            } catch (err) {
                console.error('Error fetching metadata:', err);
            }
        };

        fetchMetadata();
    }, [consolidateLocations]);

    // Fetch CPT + Provider data
    useEffect(() => {
        const fetchCptProviderData = async () => {
            setCptProviderLoading(true);
            try {
                const queryParams = new URLSearchParams();
                queryParams.append('year', cptProviderYear);
                if (cptProviderMonth) queryParams.append('month', cptProviderMonth);
                if (cptProviderCpt !== 'All') queryParams.append('cptCode', cptProviderCpt);
                if (cptProviderProvider !== 'All') queryParams.append('provider', cptProviderProvider);
                queryParams.append('section', 'cptProvider');
                queryParams.append('dateType', filters?.dateType || 'post');
                queryParams.append('consolidateLocations', consolidateLocations.toString());

                const token = localStorage.getItem('token');
                const response = await fetch(`${config.apiBaseUrl}/api/performance-analysis?${queryParams}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) throw new Error(`Failed to fetch CPT+Provider data: ${response.statusText}`);

                const data = await response.json();
                setCptProviderData(data.cptProviderData || []);
            } catch (err) {
                console.error('Error fetching CPT+Provider data:', err);
            } finally {
                setCptProviderLoading(false);
            }
        };

        fetchCptProviderData();
    }, [cptProviderYear, cptProviderMonth, cptProviderCpt, cptProviderProvider, filters?.dateType, consolidateLocations]);

    // Fetch CPT + Location data
    useEffect(() => {
        const fetchCptLocationData = async () => {
            setCptLocationLoading(true);
            try {
                const queryParams = new URLSearchParams();
                queryParams.append('year', cptLocationYear);
                if (cptLocationMonth) queryParams.append('month', cptLocationMonth);
                if (cptLocationCpt !== 'All') queryParams.append('cptCode', cptLocationCpt);
                if (cptLocationLocation !== 'All') queryParams.append('location', cptLocationLocation);
                queryParams.append('section', 'cptLocation');
                queryParams.append('dateType', filters?.dateType || 'post');
                queryParams.append('consolidateLocations', consolidateLocations.toString());

                const token = localStorage.getItem('token');
                const response = await fetch(`${config.apiBaseUrl}/api/performance-analysis?${queryParams}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) throw new Error(`Failed to fetch CPT+Location data: ${response.statusText}`);

                const data = await response.json();
                setCptLocationData(data.cptLocationData || []);
            } catch (err) {
                console.error('Error fetching CPT+Location data:', err);
            } finally {
                setCptLocationLoading(false);
            }
        };

        fetchCptLocationData();
    }, [cptLocationYear, cptLocationMonth, cptLocationCpt, cptLocationLocation, filters?.dateType, consolidateLocations]);

    // Fetch Provider Performance data
    useEffect(() => {
        const fetchProviderData = async () => {
            setProviderLoading(true);

            try {
                const queryParams = new URLSearchParams();
                queryParams.append('year', providerYear);
                if (providerMonth) queryParams.append('month', providerMonth);
                queryParams.append('section', 'provider');
                queryParams.append('dateType', filters?.dateType || 'post');
                queryParams.append('consolidateLocations', consolidateLocations.toString());

                const token = localStorage.getItem('token');
                const response = await fetch(`${config.apiBaseUrl}/api/performance-analysis?${queryParams}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) throw new Error(`Failed to fetch provider data: ${response.statusText}`);

                const data = await response.json();
                setProviderRevenueData(data.providerData?.revenue || []);
                setProviderVolumeData(data.providerData?.volume || []);
                setProviderAvgChargeData(data.providerData?.avgCharge || []);
                setProviderCptData(data.providerData?.cpt || []);
            } catch (err) {
                console.error('Error fetching provider data:', err);
            } finally {
                setProviderLoading(false);
            }
        };

        fetchProviderData();
    }, [providerYear, providerMonth, filters?.dateType, consolidateLocations]);

    // Fetch Location Performance data
    useEffect(() => {
        const fetchLocationData = async () => {
            setLocationLoading(true);

            try {
                const queryParams = new URLSearchParams();
                queryParams.append('year', locationYear);
                if (locationMonth) queryParams.append('month', locationMonth);
                queryParams.append('section', 'location');
                queryParams.append('dateType', filters?.dateType || 'post');
                queryParams.append('consolidateLocations', consolidateLocations.toString());

                const token = localStorage.getItem('token');
                const response = await fetch(`${config.apiBaseUrl}/api/performance-analysis?${queryParams}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) throw new Error(`Failed to fetch location data: ${response.statusText}`);

                const data = await response.json();
                setLocationRevenueData(data.locationData?.revenue || []);
                setLocationVolumeData(data.locationData?.volume || []);
                setLocationAvgChargeData(data.locationData?.avgCharge || []);
                setLocationCptData(data.locationData?.cpt || []);
            } catch (err) {
                console.error('Error fetching location data:', err);
            } finally {
                setLocationLoading(false);
            }
        };

        fetchLocationData();
    }, [locationYear, locationMonth, filters?.dateType, consolidateLocations]);

    const getProviderData = () => {
        switch (providerMetric) {
            case 'revenue': return providerRevenueData;
            case 'volume': return providerVolumeData;
            case 'avgCharge': return providerAvgChargeData;
            case 'cpt': return providerCptData;
            default: return providerRevenueData;
        }
    };

    const getLocationData = () => {
        switch (locationMetric) {
            case 'revenue': return locationRevenueData;
            case 'volume': return locationVolumeData;
            case 'avgCharge': return locationAvgChargeData;
            case 'cpt': return locationCptData;
            default: return locationRevenueData;
        }
    };

    const renderProviderChart = () => {
        const data = getProviderData();

        if (providerMetric === 'revenue') {
            return (
                <ComposedChart data={data} barCategoryGap="35%" >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={<CustomXAxisTick />} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} height={100} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={formatCurrency} />
                    <Tooltip content={<CustomTooltip />} cursor={false} />
                    <Bar dataKey="charges" fill="#dbeafe" stroke="#2563eb" strokeWidth={1} radius={[4, 4, 0, 0]} name="Charges" activeBar={false} />
                    <Line type="monotone" dataKey="collections" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} name="Payments" isAnimationActive={false} />
                </ComposedChart>
            );
        } else if (providerMetric === 'cpt') {
            // Custom tooltip for CPT chart showing CPT code
            const CPTTooltip = ({ active, payload }) => {
                if (active && payload && payload.length) {
                    const dataPoint = payload[0].payload;
                    return (
                        <Box
                            sx={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.5rem',
                                p: 1.5,
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.813rem', fontWeight: 600, mb: 0.5, color: '#1e293b' }}>
                                {dataPoint.name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.25 }}>
                                Top CPT: <strong style={{ color: '#1e293b' }}>{dataPoint.cptCode}</strong>
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                                Charges: <strong style={{ color: '#1e293b' }}>{formatCurrency(dataPoint.charges)}</strong>
                            </Typography>
                        </Box>
                    );
                }
                return null;
            };

            return (
                <BarChart data={data} barCategoryGap="35%" >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={<CustomXAxisTick />} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} height={100} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={formatCurrency} />
                    <Tooltip content={<CPTTooltip />} cursor={false} />
                    <Bar dataKey="charges" fill="#fef3c7" stroke="#f59e0b" strokeWidth={1} radius={[4, 4, 0, 0]} name="Top CPT Charges" activeBar={false} />
                </BarChart>
            );
        } else {
            const dataKey = providerMetric === 'volume' ? 'volume' : 'avgCharge';
            const label = providerMetric === 'volume' ? 'Patient Volume' : 'Avg Charge';
            const formatter = providerMetric === 'volume' ? (value) => value.toLocaleString() : (value) => `$${value}`;
            const fillColor = providerMetric === 'volume' ? '#d1fae5' : '#fef3c7';
            const strokeColor = providerMetric === 'volume' ? '#10b981' : '#f59e0b';

            return (
                <BarChart data={data} barCategoryGap="35%" >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={<CustomXAxisTick />} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} height={100} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={formatter} />
                    <Tooltip content={<CustomTooltip />} cursor={false} />
                    <Bar dataKey={dataKey} fill={fillColor} stroke={strokeColor} strokeWidth={1} radius={[4, 4, 0, 0]} name={label} activeBar={false} />
                </BarChart>
            );
        }
    };

    const renderLocationChart = () => {
        const data = getLocationData();

        if (locationMetric === 'revenue') {
            return (
                <ComposedChart data={data} barCategoryGap="35%" >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={<CustomXAxisTick />} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} height={100} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={formatCurrency} />
                    <Tooltip content={<CustomTooltip />} cursor={false} />
                    <Bar dataKey="charges" fill="#dbeafe" stroke="#2563eb" strokeWidth={1} radius={[4, 4, 0, 0]} name="Charges" activeBar={false} />
                    <Line type="monotone" dataKey="collections" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} name="Payments" isAnimationActive={false} />
                </ComposedChart>
            );
        } else if (locationMetric === 'cpt') {
            // Custom tooltip for CPT chart showing CPT code
            const CPTTooltip = ({ active, payload }) => {
                if (active && payload && payload.length) {
                    const dataPoint = payload[0].payload;
                    return (
                        <Box
                            sx={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.5rem',
                                p: 1.5,
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.813rem', fontWeight: 600, mb: 0.5, color: '#1e293b' }}>
                                {dataPoint.name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.25 }}>
                                Top CPT: <strong style={{ color: '#1e293b' }}>{dataPoint.cptCode}</strong>
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                                Charges: <strong style={{ color: '#1e293b' }}>{formatCurrency(dataPoint.charges)}</strong>
                            </Typography>
                        </Box>
                    );
                }
                return null;
            };

            return (
                <BarChart data={data} barCategoryGap="35%" >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={<CustomXAxisTick />} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} height={100} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={formatCurrency} />
                    <Tooltip content={<CPTTooltip />} cursor={false} />
                    <Bar dataKey="charges" fill="#fef3c7" stroke="#f59e0b" strokeWidth={1} radius={[4, 4, 0, 0]} name="Top CPT Charges" activeBar={false} />
                </BarChart>
            );
        } else {
            const dataKey = locationMetric === 'volume' ? 'volume' : 'avgCharge';
            const label = locationMetric === 'volume' ? 'Patient Volume' : 'Avg Charge';
            const formatter = locationMetric === 'volume' ? (value) => value.toLocaleString() : (value) => `$${value}`;
            const fillColor = locationMetric === 'volume' ? '#d1fae5' : '#fef3c7';
            const strokeColor = locationMetric === 'volume' ? '#10b981' : '#f59e0b';

            return (
                <BarChart data={data} barCategoryGap="35%" >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={<CustomXAxisTick />} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} height={100} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={formatter} />
                    <Tooltip content={<CustomTooltip />} cursor={false} />
                    <Bar dataKey={dataKey} fill={fillColor} stroke={strokeColor} strokeWidth={1} radius={[4, 4, 0, 0]} name={label} activeBar={false} />
                </BarChart>
            );
        }
    };

    return (
        <Box>
            {/* Row 1: Cross-Dimensional Analysis */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {/* CPT + Provider Analysis */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ boxShadow: 1, height: '100%' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                                    Charges by CPT Code + Provider
                                </Typography>
                            </Box>

                            {/* Local Filters */}
                            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                <Grid item xs={6} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Year</InputLabel>
                                        <Select value={cptProviderYear} label="Year" onChange={(e) => setCptProviderYear(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                                            {availableYears.map((yr) => (
                                                <MenuItem key={yr} value={yr.toString()} sx={{ fontSize: '0.75rem' }}>
                                                    {yr}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Month</InputLabel>
                                        <Select value={cptProviderMonth || ''} label="Month" onChange={(e) => setCptProviderMonth(e.target.value || null)} sx={{ fontSize: '0.75rem' }}>
                                            <MenuItem value="" sx={{ fontSize: '0.75rem' }}><em>All</em></MenuItem>
                                            {MONTHS.map((month, idx) => (
                                                <MenuItem key={month} value={idx + 1} sx={{ fontSize: '0.75rem' }}>
                                                    {month.substring(0, 3)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>CPT</InputLabel>
                                        <Select value={cptProviderCpt} label="CPT" onChange={(e) => setCptProviderCpt(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                                            <MenuItem value="All" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                                            {availableCpts.map(cpt => (
                                                <MenuItem key={cpt} value={cpt} sx={{ fontSize: '0.75rem' }}>{cpt}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Provider</InputLabel>
                                        <Select value={cptProviderProvider} label="Provider" onChange={(e) => setCptProviderProvider(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                                            <MenuItem value="All" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                                            {availableProviders.map(provider => (
                                                <MenuItem key={provider} value={provider} sx={{ fontSize: '0.75rem' }}>{provider}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Box sx={{ overflowX: 'auto', overflowY: 'hidden', position: 'relative' }}>
                                {cptProviderLoading && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 10
                                    }}>
                                        <CircularProgress size={40} />
                                    </Box>
                                )}
                                <ResponsiveContainer width={Math.max(600, cptProviderData.length * 50)} height={280}>
                                    <ComposedChart data={cptProviderData} barCategoryGap="35%" >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                        <XAxis dataKey="name" tick={<CustomXAxisTick />} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} height={100} interval={0} />
                                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={formatCurrency} />
                                        <Tooltip content={<CustomTooltip />} cursor={false} />
                                        <Bar dataKey="charges" fill="#dbeafe" stroke="#2563eb" strokeWidth={1} radius={[4, 4, 0, 0]} name="Charges" activeBar={false} />
                                        <Line type="monotone" dataKey="collections" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} name="Payments" isAnimationActive={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* CPT + Location Analysis */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ boxShadow: 1, height: '100%' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                                    Charges by CPT Code + Location
                                </Typography>
                            </Box>

                            {/* Local Filters */}
                            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                <Grid item xs={6} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Year</InputLabel>
                                        <Select value={cptLocationYear} label="Year" onChange={(e) => setCptLocationYear(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                                            {availableYears.map((yr) => (
                                                <MenuItem key={yr} value={yr.toString()} sx={{ fontSize: '0.75rem' }}>
                                                    {yr}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Month</InputLabel>
                                        <Select value={cptLocationMonth || ''} label="Month" onChange={(e) => setCptLocationMonth(e.target.value || null)} sx={{ fontSize: '0.75rem' }}>
                                            <MenuItem value="" sx={{ fontSize: '0.75rem' }}><em>All</em></MenuItem>
                                            {MONTHS.map((month, idx) => (
                                                <MenuItem key={month} value={idx + 1} sx={{ fontSize: '0.75rem' }}>
                                                    {month.substring(0, 3)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>CPT</InputLabel>
                                        <Select value={cptLocationCpt} label="CPT" onChange={(e) => setCptLocationCpt(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                                            <MenuItem value="All" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                                            {availableCpts.map(cpt => (
                                                <MenuItem key={cpt} value={cpt} sx={{ fontSize: '0.75rem' }}>{cpt}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Location</InputLabel>
                                        <Select value={cptLocationLocation} label="Location" onChange={(e) => setCptLocationLocation(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                                            <MenuItem value="All" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                                            {availableLocations.map(location => (
                                                <MenuItem key={location} value={location} sx={{ fontSize: '0.75rem' }}>{location}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Box sx={{ overflowX: 'auto', overflowY: 'hidden', position: 'relative' }}>
                                {cptLocationLoading && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 10
                                    }}>
                                        <CircularProgress size={40} />
                                    </Box>
                                )}
                                <ResponsiveContainer width={Math.max(600, cptLocationData.length * 50)} height={280}>
                                    <ComposedChart data={cptLocationData} barCategoryGap="35%" >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                        <XAxis dataKey="name" tick={<CustomXAxisTick />} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} height={100} interval={0} />
                                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={formatCurrency} />
                                        <Tooltip content={<CustomTooltip />} cursor={false} />
                                        <Bar dataKey="charges" fill="#dbeafe" stroke="#2563eb" strokeWidth={1} radius={[4, 4, 0, 0]} name="Charges" activeBar={false} />
                                        <Line type="monotone" dataKey="collections" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} name="Payments" isAnimationActive={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Row 2: Provider Performance & Location Performance */}
            <Grid container spacing={2.5}>
                {/* Provider Performance */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ boxShadow: 1, height: '100%' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                                    Provider Performance
                                </Typography>
                            </Box>

                            {/* Filters and Metric Pills */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                                <Stack direction="row" spacing={1.5}>
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Year</InputLabel>
                                        <Select value={providerYear} label="Year" onChange={(e) => setProviderYear(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                                            {availableYears.map((yr) => (
                                                <MenuItem key={yr} value={yr.toString()} sx={{ fontSize: '0.75rem' }}>
                                                    {yr}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Month</InputLabel>
                                        <Select value={providerMonth || ''} label="Month" onChange={(e) => setProviderMonth(e.target.value || null)} sx={{ fontSize: '0.75rem' }}>
                                            <MenuItem value="" sx={{ fontSize: '0.75rem' }}><em>All</em></MenuItem>
                                            {MONTHS.map((month, idx) => (
                                                <MenuItem key={month} value={idx + 1} sx={{ fontSize: '0.75rem' }}>
                                                    {month.substring(0, 3)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                    <Chip
                                        label="Revenue"
                                        onClick={() => setProviderMetric('revenue')}
                                        sx={{
                                            bgcolor: providerMetric === 'revenue' ? '#2563eb' : 'transparent',
                                            color: providerMetric === 'revenue' ? '#ffffff' : '#64748b',
                                            border: providerMetric === 'revenue' ? 'none' : '1px solid #e2e8f0',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: providerMetric === 'revenue' ? '#1e40af' : '#e2e8f0' }
                                        }}
                                    />
                                    <Chip
                                        label="Volume"
                                        onClick={() => setProviderMetric('volume')}
                                        sx={{
                                            bgcolor: providerMetric === 'volume' ? '#2563eb' : 'transparent',
                                            color: providerMetric === 'volume' ? '#ffffff' : '#64748b',
                                            border: providerMetric === 'volume' ? 'none' : '1px solid #e2e8f0',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: providerMetric === 'volume' ? '#1e40af' : '#e2e8f0' }
                                        }}
                                    />
                                    <Chip
                                        label="Avg Charge"
                                        onClick={() => setProviderMetric('avgCharge')}
                                        sx={{
                                            bgcolor: providerMetric === 'avgCharge' ? '#2563eb' : 'transparent',
                                            color: providerMetric === 'avgCharge' ? '#ffffff' : '#64748b',
                                            border: providerMetric === 'avgCharge' ? 'none' : '1px solid #e2e8f0',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: providerMetric === 'avgCharge' ? '#1e40af' : '#e2e8f0' }
                                        }}
                                    />
                                    <Chip
                                        label="Top CPT"
                                        onClick={() => setProviderMetric('cpt')}
                                        sx={{
                                            bgcolor: providerMetric === 'cpt' ? '#2563eb' : 'transparent',
                                            color: providerMetric === 'cpt' ? '#ffffff' : '#64748b',
                                            border: providerMetric === 'cpt' ? 'none' : '1px solid #e2e8f0',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: providerMetric === 'cpt' ? '#1e40af' : '#e2e8f0' }
                                        }}
                                    />
                                </Stack>
                            </Box>

                            <Box sx={{ overflowX: 'auto', overflowY: 'hidden', position: 'relative' }}>
                                {providerLoading && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 10
                                    }}>
                                        <CircularProgress size={40} />
                                    </Box>
                                )}
                                <ResponsiveContainer width={Math.max(600, getProviderData().length * 50)} height={280}>
                                    {renderProviderChart()}
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Location Performance */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ boxShadow: 1, height: '100%' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                                    Location Performance
                                </Typography>
                            </Box>

                            {/* Filters and Metric Pills */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                                <Stack direction="row" spacing={1.5}>
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Year</InputLabel>
                                        <Select value={locationYear} label="Year" onChange={(e) => setLocationYear(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                                            {availableYears.map((yr) => (
                                                <MenuItem key={yr} value={yr.toString()} sx={{ fontSize: '0.75rem' }}>
                                                    {yr}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Month</InputLabel>
                                        <Select value={locationMonth || ''} label="Month" onChange={(e) => setLocationMonth(e.target.value || null)} sx={{ fontSize: '0.75rem' }}>
                                            <MenuItem value="" sx={{ fontSize: '0.75rem' }}><em>All</em></MenuItem>
                                            {MONTHS.map((month, idx) => (
                                                <MenuItem key={month} value={idx + 1} sx={{ fontSize: '0.75rem' }}>
                                                    {month.substring(0, 3)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                    <Chip
                                        label="Revenue"
                                        onClick={() => setLocationMetric('revenue')}
                                        sx={{
                                            bgcolor: locationMetric === 'revenue' ? '#2563eb' : 'transparent',
                                            color: locationMetric === 'revenue' ? '#ffffff' : '#64748b',
                                            border: locationMetric === 'revenue' ? 'none' : '1px solid #e2e8f0',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: locationMetric === 'revenue' ? '#1e40af' : '#e2e8f0' }
                                        }}
                                    />
                                    <Chip
                                        label="Volume"
                                        onClick={() => setLocationMetric('volume')}
                                        sx={{
                                            bgcolor: locationMetric === 'volume' ? '#2563eb' : 'transparent',
                                            color: locationMetric === 'volume' ? '#ffffff' : '#64748b',
                                            border: locationMetric === 'volume' ? 'none' : '1px solid #e2e8f0',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: locationMetric === 'volume' ? '#1e40af' : '#e2e8f0' }
                                        }}
                                    />
                                    <Chip
                                        label="Avg Charge"
                                        onClick={() => setLocationMetric('avgCharge')}
                                        sx={{
                                            bgcolor: locationMetric === 'avgCharge' ? '#2563eb' : 'transparent',
                                            color: locationMetric === 'avgCharge' ? '#ffffff' : '#64748b',
                                            border: locationMetric === 'avgCharge' ? 'none' : '1px solid #e2e8f0',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: locationMetric === 'avgCharge' ? '#1e40af' : '#e2e8f0' }
                                        }}
                                    />
                                    <Chip
                                        label="Top CPT"
                                        onClick={() => setLocationMetric('cpt')}
                                        sx={{
                                            bgcolor: locationMetric === 'cpt' ? '#2563eb' : 'transparent',
                                            color: locationMetric === 'cpt' ? '#ffffff' : '#64748b',
                                            border: locationMetric === 'cpt' ? 'none' : '1px solid #e2e8f0',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '&:hover': { bgcolor: locationMetric === 'cpt' ? '#1e40af' : '#e2e8f0' }
                                        }}
                                    />
                                </Stack>
                            </Box>

                            <Box sx={{ overflowX: 'auto', overflowY: 'hidden', position: 'relative' }}>
                                {locationLoading && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 10
                                    }}>
                                        <CircularProgress size={40} />
                                    </Box>
                                )}
                                <ResponsiveContainer width={Math.max(600, getLocationData().length * 50)} height={280}>
                                    {renderLocationChart()}
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

export default PerformanceAnalysis;
